/**
 * LieuNaissanceAutocomplete — the INSi place-of-birth (`lieuNaissance`) field.
 *
 * Replaces the raw "type the 5-char INSEE code" input: the PS does not memorize
 * INSEE codes (SEL-MP-043 §3.4.1 EF_INS10_05 — `lieuNaissance` is the commune's
 * INSEE COG code, e.g. 63113 = Clermont-Ferrand, NOT the postal code). Instead
 * the PS types a city name and picks it; we resolve the INSEE `code` via
 * `useCommuneSearch` (geo.api.gouv.fr) and store THAT in the form.
 *
 * Form contract (kept deliberately simple so nothing downstream changes):
 *   - The `react-final-form` field value is always the bare 5-char INSEE code
 *     STRING (e.g. "63113") — exactly what `searchInsByTraits` expects for
 *     `lieuNaissance`, and what the CR02 retry + the `LIEU_NAISSANCE_REGEX` gate
 *     in InsiSearchModal already read. Selecting a commune writes `option.code`.
 *   - `freeSolo` typed-fallback: when the API can't surface a commune (gouv.fr
 *     down, a Corsican code, or a foreign-born COG-Pays 99xxx code), the PS can
 *     type a raw 5-char code. We uppercase it into the form; the modal's regex
 *     gate validates it before it may be sent.
 *
 * Display: options render as "Clermont-Ferrand (63)". Disabled until CR02 (the
 * place of birth is a refinement field, never sent on the first call). The local
 * display state (selected commune / typed text) is decoupled from the form value
 * so we can show a friendly label while storing the bare code — but it is kept in
 * sync when the value is cleared from OUTSIDE (e.g. a core trait changed and the
 * modal reset the search), so a stale place-of-birth never lingers in the UI.
 */
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import { Place as PlaceIcon } from '@mui/icons-material';

import TextField from 'components/ui/TextField';
import { useCommuneSearch } from 'services/geo/useCommuneSearch';

/** Dropdown / selected label, e.g. "Clermont-Ferrand (63)". */
const formatCommune = (c) => `${c.nom} (${c.codeDepartement})`;

/** A raw typed value is normalized the same way the wire expects: 5-char CAPS. */
const normalizeTyped = (value) => (value || '').toUpperCase();

/** Make the disabled state unmistakable (matches the shared FormTextField look). */
const disabledSx = {
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor: (theme) => theme.palette.colors?.gray100 || '#f5f5f5',
  },
  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme) => theme.palette.colors?.gray200 || '#e0e0e0',
  },
  '& .MuiInputBase-input.Mui-disabled': {
    cursor: 'not-allowed',
  },
};

/**
 * Attention state — when the field becomes the "do this next" step (CR02), give
 * it a 2px primary border so the PS's eye lands on it even before the auto-focus.
 */
const highlightSx = {
  '& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
    {
      borderColor: (theme) => theme.palette.primary.main,
      borderWidth: 2,
    },
};

export default function LieuNaissanceAutocomplete({
  name,
  disabled,
  dataId,
  placeholder,
  highlight,
  helperText,
  autoFocus,
}) {
  const { t } = useTranslation();
  const { options, loading, setQuery, clear } = useCommuneSearch();
  const { input, meta } = useField(name);

  // The visible text in the box. Controlled so a selection can show the formatted
  // commune label while the form still holds the bare INSEE code.
  const [inputValue, setInputValue] = useState('');
  // The selected commune object (for display only) — null on a typed-fallback.
  const [selected, setSelected] = useState(null);

  // Move the cursor into the field the moment it becomes the next step (CR02).
  // Fire on the false→true transition only, so re-renders don't steal focus back.
  const inputRef = useRef(null);
  const wasAutoFocus = useRef(false);
  useEffect(() => {
    if (autoFocus && !wasAutoFocus.current && inputRef.current) {
      inputRef.current.focus();
    }
    wasAutoFocus.current = autoFocus;
  }, [autoFocus]);

  // Keep the display in sync when the field is cleared from outside (the modal
  // resets `lieuNaissance` whenever a core trait changes). Without this the old
  // commune label / typed code would stay on screen even though it is no longer
  // part of the form — and won't be sent on the next first-search.
  useEffect(() => {
    if (!input.value && (selected || inputValue)) {
      setSelected(null);
      setInputValue('');
      clear();
    }
    // Only react to external value changes; local edits set both together.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  const handleInputChange = (_event, value, reason) => {
    setInputValue(value);
    if (reason === 'input') {
      // PS is typing → debounced commune lookup + treat the text as a
      // typed-fallback code (uppercased) so a manual INSEE/COG-Pays entry
      // is already in the form even without picking an option.
      setSelected(null);
      setQuery(value);
      input.onChange(normalizeTyped(value));
    } else if (reason === 'clear') {
      setSelected(null);
      clear();
      input.onChange('');
    }
  };

  const handleChange = (_event, value) => {
    if (value && typeof value === 'object') {
      // Picked a commune from the dropdown.
      setSelected(value);
      setInputValue(formatCommune(value));
      input.onChange(value.code);
      clear();
    } else if (typeof value === 'string') {
      // freeSolo commit (Enter on raw text).
      setSelected(null);
      input.onChange(normalizeTyped(value));
    } else {
      setSelected(null);
      input.onChange('');
    }
  };

  const showError = (meta.touched || meta.submitFailed) && meta.error;
  // The field carries the "do this next" message in CR02; an error always wins.
  const fieldHelper = showError ? t(meta.error) : helperText || undefined;

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      data-id={dataId}
      options={options}
      // Server-side search — never filter the gouv.fr results client-side.
      filterOptions={(x) => x}
      loading={loading}
      loadingText={t('Searching…')}
      noOptionsText={t('Type a city name')}
      // Display: the selected commune label, else whatever code is in the form.
      value={selected || input.value || null}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={input.onBlur}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : formatCommune(option)
      }
      isOptionEqualToValue={(option, value) =>
        option.code === (typeof value === 'object' ? value.code : value)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="outlined"
          size="small"
          sx={highlight ? { ...disabledSx, ...highlightSx } : disabledSx}
          placeholder={placeholder || t('Start typing a city…')}
          error={Boolean(showError)}
          helperText={fieldHelper}
          FormHelperTextProps={{
            sx: {
              color:
                highlight && !showError
                  ? (theme) => theme.palette.primary.main
                  : undefined,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <PlaceIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            'data-testid': dataId,
          }}
        />
      )}
    />
  );
}

LieuNaissanceAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  dataId: PropTypes.string,
  placeholder: PropTypes.string,
  /** Draw the attention (primary) border — used when the field becomes the CR02 step. */
  highlight: PropTypes.bool,
  /** Helper line under the field (already translated). An error message overrides it. */
  helperText: PropTypes.string,
  /** Focus the input on the false→true transition (e.g. when CR02 arrives). */
  autoFocus: PropTypes.bool,
};

LieuNaissanceAutocomplete.defaultProps = {
  disabled: false,
  dataId: 'insi-lieu-naissance',
  placeholder: undefined,
  highlight: false,
  helperText: undefined,
  autoFocus: false,
};
